import { CheckCircleTwoTone, DownOutlined } from '@ant-design/icons';
import { Dropdown } from 'antd';
import { type ItemType } from 'antd/es/menu/interface';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { DataTableContext } from 'components/DataTable';
import { StyledTitle } from 'components/DataTable/Views/Views.style';
import { Button, Tooltip } from 'components/ui';
import {
  type AtlasGqlTableView,
  useConfiguredMakeModelCombinationsQuery,
} from 'types/atlas-graphql';

import { getRelevantBladeCards } from './getRelevantBladeCards';
import { useBladeCardViews } from './useBladeCardViews';

// The Sites or Assets the table is scoped to. Only Defect Patterns for the Make/Models
// configured there are offered.
export type BladeCardsScope = {
  siteIds?: string[];
  assetIds?: string[];
};

type BladeCardsProps = BladeCardsScope & {
  tableId?: string;
  applyViewToTable: (tableViewToApply?: AtlasGqlTableView) => void;
  onSelect?: () => void;
};

export const BladeCards: React.FC<BladeCardsProps> = ({
  siteIds,
  assetIds,
  tableId,
  applyViewToTable,
  onSelect,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { tableView } = useContext(DataTableContext);

  const { views, loading } = useBladeCardViews();

  // An Asset scope is narrower than its Site, so it wins when both are set.
  const { data: turbineMakeModelData } = useConfiguredMakeModelCombinationsQuery({
    variables: {
      input: {
        assetTypeName: 'Turbine',
        ...(assetIds?.length ? { assetIds } : siteIds?.length ? { siteIds } : {}),
      },
    },
  });
  const { data: bladeMakeModelData } = useConfiguredMakeModelCombinationsQuery({
    variables: {
      input: {
        assetTypeName: 'Blade',
        ...(assetIds?.length ? { ancestorAssetIds: assetIds } : siteIds?.length ? { siteIds } : {}),
      },
    },
  });

  const relevantViews = getRelevantBladeCards(
    views,
    turbineMakeModelData?.configuredMakeModelCombinations.existingValues ?? [],
    bladeMakeModelData?.configuredMakeModelCombinations.existingValues ?? []
  );

  const items: ItemType[] = relevantViews.map(view => ({
    key: view.id,
    label: (
      <StyledTitle>
        {view.id === tableView?.id && <CheckCircleTwoTone twoToneColor={theme.green} />}
        <span className="blade-card-name">{view.name}</span>
      </StyledTitle>
    ),
    onClick: () => {
      if (tableId) {
        applyViewToTable({ ...view, tableName: tableId });
      }
      if (onSelect) {
        onSelect();
      }
    },
  }));

  const tooltipMessage = !!items.length
    ? t(
        'damage.defectPatternsTooltip',
        'Data indicates the Damages within these regions may see higher risks of rapid Damage Propagation over time.'
      )
    : t(
        'damage.defectPatternsEmptyTooltip',
        'There are no known Defect Patterns that match your Damage data. Filling in Blade Make/Model on your Assets may make some available.'
      );

  return (
    <Dropdown
      menu={{ items, style: { maxHeight: 350, overflowY: 'scroll' } }}
      trigger={['click']}
      placement="bottomLeft"
    >
      <Tooltip title={tooltipMessage}>
        <Button data-testid="blade-cards-dropdown" disabled={!items.length} loading={loading}>
          {t('damage.defectPatterns', 'Defect Patterns')} <DownOutlined />
        </Button>
      </Tooltip>
    </Dropdown>
  );
};
